/* eslint-disable */
import { useState } from "react";
import axios from "axios";
import { ExtractAtomValue, useAtom } from "jotai";
import globalState from "../../../store";
import { IService, ServiceAreaArray } from "../../../types/types";

interface GenerateProps {
  tagline: boolean;
  disabled?: boolean;
}

const Generate: React.FC<GenerateProps> = ({ tagline, disabled = false }) => {
  const [loading, setLoading] = useState(false);
  const [globalStateAtom, setGlobalStateAtom] = useAtom(globalState);

  const generate = async () => {
    try {
      setLoading(true);

      if (tagline) {
        const company_tagline = await generateTagline(globalStateAtom);
        setGlobalStateAtom({ ...globalStateAtom, company_tagline });
      } else {
        const about = await generateAboutUs(globalStateAtom);
        setGlobalStateAtom({ ...globalStateAtom, about });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={generate}
      className={
        disabled ? "text-gray-400 cursor-not-allowed" : "cursor-pointer"
      }
      disabled={disabled}
    >
      {loading ? (
        <img src="/loading.gif" width="20" alt="loading" />
      ) : (
        <p>Generate</p>
      )}
    </button>
  );
};

export async function generateTagline(
  globalStateAtom: ExtractAtomValue<typeof globalState>
) {
  try {
    const location = getLocation(globalStateAtom.service_area_array);
    const response = await axios.get<{ message: string }>(
      `${process.env.REACT_APP_SERVER_URL}/api/landing-pages/generate-tagline`,
      {
        params: {
          company: globalStateAtom.company_name,
          location,
          industry:
            globalStateAtom.company_type ||
            globalStateAtom?.industry_alias?.alias,
          primary_keyword: globalStateAtom.primary_keyword,
        },
      }
    );
    return response.data.message;
  } catch (error) {
    alert(
      `Error generating: Verify that Company Name, Industry and at least one Service Area are filled.`
    );
    throw error;
  }
}

export async function generateAboutUs(
  globalStateAtom: ExtractAtomValue<typeof globalState>
) {
  try {
    const location = getLocation(globalStateAtom.service_area_array);
    const response = await axios.get<{ message: string }>(
      `${process.env.REACT_APP_SERVER_URL}/api/landing-pages/generate-about-us`,
      {
        params: {
          company: globalStateAtom.company_name,
          location,
          industry:
            globalStateAtom.company_type ||
            globalStateAtom?.industry_alias?.alias,
          keywords: globalStateAtom.keywords,
        },
      }
    );
    return response.data.message;
  } catch (error) {
    alert(
      `Error generating: Verify that Company Name, Industry and at least one Service Area are filled.`
    );
    throw error;
  }
}

export async function generateMetadataTitle(
  globalStateAtom: ExtractAtomValue<typeof globalState>
) {
  try {
    const [city, state] = getCityState(globalStateAtom.service_area_array);
    const response = await axios.get<{ message: string }>(
      `${process.env.REACT_APP_SERVER_URL}/api/seo/generate-meta-title`,
      {
        params: {
          company_name: globalStateAtom.company_name,
          company_type:
            globalStateAtom.company_type ||
            globalStateAtom?.industry_alias?.alias,
          city,
          state,
          primary_keyword: globalStateAtom.primary_keyword,
        },
      }
    );
    return response.data.message;
  } catch (error) {
    alert(
      `Error generating: Verify that Company Name, Industry and at least one Service Area are filled.`
    );
    throw error;
  }
}

export async function generateMetadataDescription(
  globalStateAtom: ExtractAtomValue<typeof globalState>
) {
  try {
    const [city, state] = getCityState(globalStateAtom.service_area_array);
    const response = await axios.get<{ message: string }>(
      `${process.env.REACT_APP_SERVER_URL}/api/seo/generate-meta-description`,
      {
        params: {
          company_name: globalStateAtom.company_name,
          company_type:
            globalStateAtom.company_type ||
            globalStateAtom?.industry_alias?.alias,
          city,
          state,
          primary_keyword: globalStateAtom.primary_keyword,
        },
      }
    );
    return response.data.message;
  } catch (error) {
    alert(
      `Error generating: Verify that Company Name, Industry and at least one Service Area are filled.`
    );
    throw error;
  }
}

function getLocation(service_area_array: ServiceAreaArray[]) {
  if (!service_area_array || !service_area_array.length) return "";
  const [city, state] = service_area_array[0];
  return city || state ? `${city}, ${state}` : "";
}

function getCityState(
  service_area_array: ServiceAreaArray[]
): [string, string] {
  if (!service_area_array || !service_area_array.length) return ["", ""];
  const [city, state] = service_area_array[0];
  return [city, state];
}

export const generateFaqs = async (
  globalStateAtom: ExtractAtomValue<typeof globalState>
) => {
  try {
    const response = await axios.get<{ message: string }>(
      `${process.env.REACT_APP_SERVER_URL}/api/landing-pages/generate-faqs`,
      {
        params: {
          company_name: globalStateAtom.company_name,
          company_type:
            globalStateAtom.company_type ||
            globalStateAtom?.industry_alias?.alias,
        },
      }
    );
    return response.data.message;
  } catch (error) {
    alert(
      `Error generating: Verify that Company Name, Company Type are filled.`
    );
    throw error;
  }
};

const generateServiceDescription = async (
  globalStateAtom: ExtractAtomValue<typeof globalState>,
  service: IService,
  type: "short" | "long" | "longV2"
): Promise<string> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/services/generate-description`,
      {
        params: {
          type: type,
          industry:
            globalStateAtom.company_type ||
            globalStateAtom?.industry_alias?.alias,
          service: service?.["service-name"],
          company_name: globalStateAtom.company_name,
          primary_keyword: service.primary_keyword,
          keywords: service.keywords,
        },
      }
    );
    return response?.data?.message || "";
  } catch (error) {
    alert(
      `Error generating: Verify that Company Name, Industry and Service Name are filled.`
    );
    throw error;
  }
};

export const regenerateServiceDescriptions = async (
  globalStateAtom: ExtractAtomValue<typeof globalState>
): Promise<IService[]> => {
  try {
    const updatedServices = await Promise.all(
      globalStateAtom.services.map(async (service) => {
        return Promise.all([
          generateServiceDescription(globalStateAtom, service, "short"),
          generateServiceDescription(globalStateAtom, service, "longV2"),
        ]).then(([shortDescription, longDescription]) => {
          return {
            ...service,
            "service-description": shortDescription,
            "service-description-long": longDescription,
          };
        });
      })
    );
    return updatedServices;
  } catch (error) {
    alert("Error regenerating service descriptions.");
    throw error;
  }
};

export default Generate;
